import * as THREE from 'three';
import { CHUNK_SIZE, WATER_LEVEL, sampleBiome, sampleHeight, generateChunkResources, type ResourceInstance, type WorldParams } from './WorldGen';
import { resourceDefs } from '../data/resources';
import { lambert, resourceGeometry } from './Assets';

/**
 * Tek bir dünya parçası (chunk).
 * - Arazi: vertex renkli tek mesh (biyom geçişleri ucuz şekilde harmanlanır)
 * - Bitki/kaya/maden: tür başına InstancedMesh (yüz binlerce nesne için tek draw call)
 * Toplanan kaynak, mesh yeniden kurulmadan ölçeği 0'a çekilerek gizlenir.
 */

const HIDDEN = new THREE.Matrix4().makeScale(0, 0, 0);

export interface ResourceHit {
  instance: ResourceInstance;
  chunkKey: string;
}

export class Chunk {
  readonly group = new THREE.Group();
  readonly key: string;
  /** Bu chunk'taki kaynaklar (toplananlar dahil; gizli olanlar hidden set'inde). */
  resources: ResourceInstance[] = [];
  private instanced = new Map<string, THREE.InstancedMesh>();
  /** resource id -> { mesh, index } */
  private slot = new Map<string, { mesh: THREE.InstancedMesh; index: number }>();
  private terrain: THREE.Mesh | null = null;
  private segments = 0;
  private disposed = false;

  constructor(readonly cx: number, readonly cz: number) {
    this.key = chunkKey(cx, cz);
    this.group.name = `chunk_${this.key}`;
    this.group.position.set(cx * CHUNK_SIZE, 0, cz * CHUNK_SIZE);
    this.group.matrixAutoUpdate = false;
    this.group.updateMatrix();
  }

  get built(): boolean {
    return this.terrain !== null;
  }
  get lodSegments(): number {
    return this.segments;
  }

  /** Araziyi kurar. segments = LOD çözünürlüğü. */
  buildTerrain(p: WorldParams, segments: number): void {
    if (this.disposed) return;
    if (this.terrain && this.segments === segments) return;
    if (this.terrain) {
      this.group.remove(this.terrain);
      this.terrain.geometry.dispose();
      this.terrain = null;
    }
    this.segments = segments;

    let geo: THREE.BufferGeometry = new THREE.PlaneGeometry(CHUNK_SIZE, CHUNK_SIZE, segments, segments);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const colors = new Float32Array(pos.count * 3);
    const originX = this.cx * CHUNK_SIZE;
    const originZ = this.cz * CHUNK_SIZE;
    const col = new THREE.Color();

    for (let i = 0; i < pos.count; i++) {
      const lx = pos.getX(i);
      const lz = pos.getZ(i);
      const wx = originX + lx + CHUNK_SIZE / 2;
      const wz = originZ + lz + CHUNK_SIZE / 2;
      const s = sampleBiome(p, wx, wz);
      pos.setY(i, s.height);

      // Renk: biyom iki tonu arasında yükseklik/gürültüyle harman
      const t = Math.max(0, Math.min(1, (s.height % 6) / 6));
      col.setHex(s.biome.ground).lerp(new THREE.Color(s.biome.ground2), t);
      if (s.height < WATER_LEVEL + 0.4) col.lerp(new THREE.Color(0xd9c68c), 0.55); // kıyı kumu
      if (s.height < WATER_LEVEL - 2) col.lerp(new THREE.Color(0x2b4a63), 0.5); // su altı
      colors[i * 3] = col.r;
      colors[i * 3 + 1] = col.g;
      colors[i * 3 + 2] = col.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geo.computeVertexNormals();
    // Etek geometriye birleştirilir: LOD yarıkları kapanır fakat chunk başına
    // ikinci draw call oluşmaz.
    geo = mergeTerrainGeometry(geo, makeTerrainSkirt(pos, colors, segments));

    const mesh = new THREE.Mesh(geo, terrainMaterial());
    mesh.position.set(CHUNK_SIZE / 2, 0, CHUNK_SIZE / 2);
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    this.group.add(mesh);
    this.terrain = mesh;
  }

  /** Kaynakları üretir ve instanced mesh'leri kurar. */
  buildResources(p: WorldParams, harvested: ReadonlySet<string>, density: number, castShadow: boolean): void {
    if (this.disposed || this.instanced.size > 0) return;
    this.resources = generateChunkResources(p, this.cx, this.cz);

    // Yoğunluk ayarı: kalite düşükse dekoratif bitkileri seyrelt (madenler korunur)
    const kept = this.resources.filter((r) => {
      const def = resourceDefs.get(r.def);
      if (!def) return false;
      if (def.kind === 'ore' || def.kind === 'tree' || def.kind === 'rock') return true;
      return hash01(r.id) <= density;
    });

    const byDef = new Map<string, ResourceInstance[]>();
    for (const r of kept) {
      let list = byDef.get(r.def);
      if (!list) byDef.set(r.def, (list = []));
      list.push(r);
    }

    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const v = new THREE.Vector3();
    const one = new THREE.Vector3();
    const originX = this.cx * CHUNK_SIZE;
    const originZ = this.cz * CHUNK_SIZE;

    for (const [defId, list] of byDef) {
      const def = resourceDefs.get(defId);
      if (!def) continue;
      const geo = resourceGeometry(def);
      const mesh = new THREE.InstancedMesh(geo, lambert(def.color), list.length);
      mesh.castShadow = castShadow;
      mesh.receiveShadow = false;
      mesh.frustumCulled = true;
      mesh.name = defId;
      for (let i = 0; i < list.length; i++) {
        const r = list[i]!;
        if (harvested.has(r.id)) {
          mesh.setMatrixAt(i, HIDDEN);
        } else {
          q.setFromAxisAngle(UP, r.rotY);
          v.set(r.x - originX, r.y, r.z - originZ);
          one.setScalar(r.scale * def.size);
          m.compose(v, q, one);
          mesh.setMatrixAt(i, m);
        }
        this.slot.set(r.id, { mesh, index: i });
      }
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
      this.group.add(mesh);
      this.instanced.set(defId, mesh);
    }
    this.resources = kept;
  }

  /** Toplanan kaynağı görsel olarak kaldırır (mesh yeniden kurulmaz). */
  hideResource(id: string): void {
    const s = this.slot.get(id);
    if (!s) return;
    s.mesh.setMatrixAt(s.index, HIDDEN);
    s.mesh.instanceMatrix.needsUpdate = true;
  }

  /** Yenilenen kaynağı geri getirir. */
  showResource(id: string): void {
    const s = this.slot.get(id);
    const r = this.resources.find((x) => x.id === id);
    if (!s || !r) return;
    const def = resourceDefs.get(r.def);
    if (!def) return;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion().setFromAxisAngle(UP, r.rotY);
    m.compose(new THREE.Vector3(r.x - this.cx * CHUNK_SIZE, r.y, r.z - this.cz * CHUNK_SIZE), q, new THREE.Vector3().setScalar(r.scale * def.size));
    s.mesh.setMatrixAt(s.index, m);
    s.mesh.instanceMatrix.needsUpdate = true;
  }

  /** Raycast hedefleri (etkileşim). */
  pickables(): THREE.Object3D[] {
    return [...this.instanced.values()];
  }

  resourceByInstance(mesh: THREE.InstancedMesh, index: number): ResourceInstance | null {
    for (const [id, s] of this.slot) {
      if (s.mesh === mesh && s.index === index) return this.resources.find((r) => r.id === id) ?? null;
    }
    return null;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.terrain) {
      this.terrain.geometry.dispose();
      this.group.remove(this.terrain);
      this.terrain = null;
    }
    for (const mesh of this.instanced.values()) {
      mesh.dispose(); // instance buffer'ları serbest bırakır (geometri paylaşımlı, dokunulmaz)
      this.group.remove(mesh);
    }
    this.instanced.clear();
    this.slot.clear();
    this.resources = [];
    this.group.clear();
  }
}

const UP = new THREE.Vector3(0, 1, 0);

let _terrainMat: THREE.Material | null = null;
function terrainMaterial(): THREE.Material {
  if (!_terrainMat) {
    _terrainMat = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: false });
  }
  return _terrainMat;
}

/** Kenar vertexlerini aşağı uzatarak farklı LOD meshleri arasındaki boşluğu örter. */
function makeTerrainSkirt(surface: THREE.BufferAttribute, colors: Float32Array, segments: number): THREE.BufferGeometry {
  const positions: number[] = [];
  const vertexColors: number[] = [];
  const width = segments + 1;
  const put = (index: number, lowered: boolean) => {
    const y = surface.getY(index);
    positions.push(surface.getX(index), lowered ? Math.min(WATER_LEVEL - 48, y - 48) : y, surface.getZ(index));
    const shade = lowered ? 0.72 : 1;
    vertexColors.push(colors[index * 3]! * shade, colors[index * 3 + 1]! * shade, colors[index * 3 + 2]! * shade);
  };
  const edge = (a: number, b: number) => {
    // İki üçgen: üst kenar ve onun aşağı kopyası. Aynı iki üçgenin ters
    // sarımlı kopyası yalnız perdeyi çift yönlü yapar; bütün terrain'i
    // DoubleSide çizmenin mobil maliyetini taşımaz.
    put(a, false); put(b, false); put(b, true);
    put(a, false); put(b, true); put(a, true);
    put(b, true); put(b, false); put(a, false);
    put(a, true); put(b, true); put(a, false);
  };
  for (let i = 0; i < segments; i++) {
    edge(i, i + 1); // kuzey
    edge(segments * width + i + 1, segments * width + i); // güney
    edge(i * width, (i + 1) * width); // batı
    edge((i + 1) * width + segments, i * width + segments); // doğu
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(vertexColors, 3));
  geo.computeVertexNormals();
  return geo;
}

/** Indexed surface + non-indexed skirt tek terrain meshinde birleştirilir. */
function mergeTerrainGeometry(surface: THREE.BufferGeometry, skirt: THREE.BufferGeometry): THREE.BufferGeometry {
  const flat = surface.toNonIndexed();
  const aPos = flat.getAttribute('position') as THREE.BufferAttribute;
  const aCol = flat.getAttribute('color') as THREE.BufferAttribute;
  const bPos = skirt.getAttribute('position') as THREE.BufferAttribute;
  const bCol = skirt.getAttribute('color') as THREE.BufferAttribute;
  const positions = new Float32Array((aPos.count + bPos.count) * 3);
  const colors = new Float32Array((aCol.count + bCol.count) * 3);
  positions.set(aPos.array as Float32Array, 0);
  positions.set(bPos.array as Float32Array, aPos.count * 3);
  colors.set(aCol.array as Float32Array, 0);
  colors.set(bCol.array as Float32Array, aCol.count * 3);
  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  merged.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  merged.computeVertexNormals();
  flat.dispose();
  surface.dispose();
  skirt.dispose();
  return merged;
}

export function chunkKey(cx: number, cz: number): string {
  return `${cx},${cz}`;
}

export function worldToChunk(x: number, z: number): { cx: number; cz: number } {
  return { cx: Math.floor(x / CHUNK_SIZE), cz: Math.floor(z / CHUNK_SIZE) };
}

/** Kimlikten 0..1 deterministik değer (yoğunluk seyreltme için). */
function hash01(id: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

export { sampleHeight };
